import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Box,
  Typography,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import WebRecurringSchedule from './WebRecurringSchedule';

interface AvailabilityModalProps {
  open: boolean;
  onClose: () => void;
  userID: string;
}

const AvailabilityModal: React.FC<AvailabilityModalProps> = ({
  open,
  onClose,
  userID,
}) => {
  const theme = useTheme();
  const [isSaving, setIsSaving] = useState(false);
  const recurringScheduleRef = React.useRef<any>(null);

  console.log('AvailabilityModal render:', { open, userID, theme });

  const handleSave = () => {
    setIsSaving(true);
    // Trigger save from WebRecurringSchedule component
    if (recurringScheduleRef.current) {
      recurringScheduleRef.current.save();
    } else {
      setIsSaving(false);
    }
  };

  const handleSaveComplete = (success: boolean) => {
    setIsSaving(false);
    if (success) {
      onClose();
    }
  };

  if (!open) {
    console.log('Modal not open, returning null');
    return null;
  }

  console.log('Rendering AvailabilityModal');

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      style={{ zIndex: 1 }}
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.12)',
          minHeight: '600px',
          backgroundColor: theme.custom.background1,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 2,
          borderBottom: `1px solid ${theme.custom.stroke}`,
          backgroundColor: theme.custom.background1,
          color: theme.custom.text,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Working Hours
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{
            color: theme.custom.text,
            '&:hover': {
              backgroundColor: theme.custom.fill1,
            },
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          p: 0,
          backgroundColor: theme.custom.background1,
          overflow: 'hidden',
        }}
      >
        <Box sx={{ height: '500px', position: 'relative', p: 3 }}>
          {userID ? (
            <React.Suspense fallback={<div>Loading schedule...</div>}>
              <WebRecurringSchedule
                userID={userID}
                onSaveComplete={handleSaveComplete}
                ref={recurringScheduleRef}
              />
            </React.Suspense>
          ) : (
            <div>Loading user information...</div>
          )}
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          borderTop: `1px solid ${theme.custom.stroke}`,
          backgroundColor: theme.custom.background1,
          gap: 2,
        }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            color: theme.custom.text,
            borderColor: theme.custom.stroke,
            '&:hover': {
              borderColor: theme.custom.text,
              backgroundColor: theme.custom.fill1,
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={isSaving}
          sx={{
            backgroundColor: theme.custom.primary,
            color: theme.custom.textCTA,
            '&:hover': {
              backgroundColor: theme.custom.primaryDark,
            },
            '&:disabled': {
              backgroundColor: theme.custom.fill2,
              color: theme.custom.secondary,
            },
          }}
        >
          {isSaving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AvailabilityModal;
